var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useEffect, useCallback, } from 'react';
import shortid from 'shortid';
import styled from '../../styled';
import Tag from '../tag';
import { Chevron } from '../icons/outline';
const KEY_CODE = {
    BACKSPACE: 8,
    DELETE: 46,
    ENTER: 13,
    COMMA: 188,
    LEFT: 37,
    RIGHT: 39,
};
const getBorderColor = (theme, focus, error) => {
    if (error) {
        return theme.colors.RED_500;
    }
    if (focus) {
        return theme.colors.PRIMARY_600;
    }
    return theme.colors.INK_300;
};
const Container = styled.div `
  display: flex;
  width: 100%;
  border-radius: 6px;
  padding: ${({ contentPadding }) => contentPadding || '0.25rem 0.75rem 0.125rem'};
  background-color: ${({ theme }) => theme.colors.SNOW_100};
  border: 0.0625rem solid
    ${({ theme, focus, error }) => getBorderColor(theme, focus, error)};
  box-sizing: border-box;
  align-items: center;

  :hover {
    cursor: pointer;
    > svg {
      fill: ${({ theme }) => theme.colors.PRIMARY_600};
    }
  }

  > svg {
    transition: transform 0.3s ease-in-out;
  }
`;
const InputWrapper = styled.div `
  display: inline-flex;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
`;
const StyledTag = styled(Tag) `
  margin: 0 0.125rem 0.125rem 0;
`;
const StyledInput = styled.input `
  padding: 0.125rem 0 0.25rem;
  height: 1.5rem;
  border: none;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme, error }) => error ? theme.colors.RED_700 : theme.colors.INK_800};
  flex: 1;

  ::placeholder {
    color: ${({ theme }) => theme.colors.INK_400};
    font-size: 1rem;
    line-height: 1.5;
  }

  :focus {
    outline: none;
  }
`;
const StyledDiv = styled.div `
  padding: 0.125rem 0 0.25rem;
  height: 1.5rem;
  border: none;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.INK_800};
  flex: 1;

  ::placeholder {
    color: ${({ theme }) => theme.colors.INK_400};
    font-size: 1rem;
    line-height: 1.5;
  }

  :focus {
    outline: none;
  }
`;
const TagEditor = (_a) => {
    var { placeHolder, tags, shouldReplaceSpacesWithDashes = false, ErrorIcon, initInput = '', disabledTagsIndex = [], contentPadding, onTagChange, onValidation, renderErrorMessage, highLightedTags = [], isOpen = false, selectorMode = false, e2eContainerId, e2eTagId, e2eTagCloseButtonId } = _a, restProps = __rest(_a, ["placeHolder", "tags", "shouldReplaceSpacesWithDashes", "ErrorIcon", "initInput", "disabledTagsIndex", "contentPadding", "onTagChange", "onValidation", "renderErrorMessage", "highLightedTags", "isOpen", "selectorMode", "e2eContainerId", "e2eTagId", "e2eTagCloseButtonId"]);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState(initInput);
    const [state, setState] = useState({
        isFocus: false,
        isError: !onValidation(inputValue, tags),
        selectedTagIndex: tags.length,
    });
    const { isFocus, isError, selectedTagIndex } = state;
    const handleClickOutside = useCallback(event => {
        // handle create tag and un-focus
        if (['path', 'svg'].includes(event.target.tagName) ||
            (event.target.tagName === 'DIV' &&
                event.target.firstChild &&
                event.target.firstChild.nodeName === 'svg')) {
            event.preventDefault();
        }
        else if (containerRef.current &&
            !containerRef.current.contains(event.target) &&
            inputRef.current &&
            inputRef.current.value) {
            let newSelectedTagIndex = selectedTagIndex;
            const isNewError = !onValidation(inputRef.current.value, tags);
            if (!isNewError) {
                const tag = shouldReplaceSpacesWithDashes
                    ? inputRef.current.value.replace(' ', '-')
                    : inputRef.current.value;
                const newTags = [...tags, tag];
                onTagChange(newTags);
                newSelectedTagIndex = newTags.length;
                setInputValue('');
            }
            setState({
                isFocus: false,
                isError: isNewError,
                selectedTagIndex: newSelectedTagIndex,
            });
        }
        else if (containerRef.current &&
            !containerRef.current.contains(event.target) &&
            isFocus) {
            setState({
                isFocus: false,
                isError: false,
                selectedTagIndex: tags.length,
            });
        }
    }, [
        isFocus,
        selectedTagIndex,
        onValidation,
        tags,
        shouldReplaceSpacesWithDashes,
        onTagChange,
    ]);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('mousedown', handleClickOutside);
            return () => {
                window.removeEventListener('mousedown', handleClickOutside);
            };
        }
        return () => { };
    }, [handleClickOutside]);
    useEffect(() => {
        setState(Object.assign(Object.assign({}, state), { selectedTagIndex: tags.length }));
    }, [tags.length]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleTagCloseClick = (e, idx) => () => {
        if (inputRef.current) {
            const newTags = tags.filter((_, index) => index !== idx);
            onTagChange(newTags);
            const validation = onValidation(inputRef.current.value, newTags);
            inputRef.current.focus();
            setState({
                isFocus: true,
                isError: !validation,
                selectedTagIndex: newTags.length,
            });
            e.stopPropagation();
        }
    };
    const handleClick = (event) => {
        const target = event.target;
        if (['path', 'svg'].includes(target.tagName) ||
            (target.tagName === 'DIV' &&
                target.firstChild &&
                target.firstChild.nodeName === 'svg')) {
            event.preventDefault();
        }
        else {
            if (inputRef.current) {
                inputRef.current.focus();
            }
            setState(Object.assign(Object.assign({}, state), { isFocus: true }));
        }
    };
    const handleKeyDown = (event) => {
        if (inputRef.current) {
            const { keyCode, target } = event;
            let isNewError = false;
            let newSelectedTagIndex = selectedTagIndex;
            // prevent insert comma in input tag
            if (keyCode === KEY_CODE.COMMA) {
                event.preventDefault();
            }
            // handle change on selected tag
            if (!inputRef.current.value &&
                selectedTagIndex >= 1 &&
                target.selectionStart === 0 &&
                [KEY_CODE.LEFT, KEY_CODE.BACKSPACE, KEY_CODE.DELETE].includes(keyCode)) {
                newSelectedTagIndex -= 1;
            }
            else if (!inputRef.current.value &&
                selectedTagIndex <= tags.length - 1 &&
                target.selectionStart ===
                    inputRef.current.value.length &&
                keyCode === KEY_CODE.RIGHT) {
                newSelectedTagIndex += 1;
            }
            // handle create or delete tag
            if (inputRef.current.value &&
                [KEY_CODE.ENTER, KEY_CODE.COMMA].includes(keyCode)) {
                isNewError = !onValidation(inputRef.current.value, tags);
                if (!isNewError) {
                    const tag = shouldReplaceSpacesWithDashes
                        ? inputRef.current.value.replace(' ', '-')
                        : inputRef.current.value;
                    const newTags = [...tags, tag];
                    onTagChange(newTags);
                    newSelectedTagIndex = newTags.length;
                    setInputValue('');
                }
            }
            else if (tags.length > 0 &&
                !inputRef.current.value &&
                selectedTagIndex !== tags.length &&
                [KEY_CODE.BACKSPACE, KEY_CODE.DELETE].includes(keyCode)) {
                const newTags = tags.filter((_, index) => index !== selectedTagIndex || disabledTagsIndex.includes(index));
                onTagChange(newTags);
                newSelectedTagIndex = newTags.length;
            }
            setState(Object.assign(Object.assign({}, state), { isError: isNewError, selectedTagIndex: newSelectedTagIndex }));
        }
    };
    const getTagBackgroundColor = (tag, idx) => {
        if (highLightedTags.length > 0 && highLightedTags.includes(tag)) {
            return 'RED_500';
        }
        return idx === selectedTagIndex && !selectorMode ? 'RED_500' : 'INK_200';
    };
    return (React.createElement("div", Object.assign({}, restProps),
        React.createElement(Container, { "data-e2e-id": e2eContainerId, focus: isFocus, error: isError, contentPadding: contentPadding, onClick: handleClick, ref: containerRef },
            React.createElement(InputWrapper, null,
                tags.length >= 1 &&
                    tags.map((tag, idx) => (React.createElement(StyledTag, { e2eTagId: e2eTagId, e2eCloseButtonId: e2eTagCloseButtonId, key: shortid.generate(), size: "MEDIUM", lang: "en", label: tag, onCloseClick: !disabledTagsIndex.includes(idx)
                            ? e => handleTagCloseClick(e, idx)()
                            : undefined, bgColor: getTagBackgroundColor(tag, idx) }))),
                selectorMode ? (React.createElement(StyledDiv, { ref: inputRef }, !tags.length ? placeHolder : '')) : (React.createElement(StyledInput, { error: isError, ref: inputRef, placeholder: !tags.length ? placeHolder : '', onKeyDown: handleKeyDown, value: inputValue, onChange: e => {
                        setInputValue(e.target.value);
                    } }))),
            selectorMode && (React.createElement(Chevron, { position: isOpen ? 'TOP' : 'BOTTOM', size: "MEDIUM" })),
            isError && ErrorIcon && React.createElement(ErrorIcon, { size: "SMALL", color: "RED_600" })),
        isError && renderErrorMessage(inputValue)));
};
export default TagEditor;
//# sourceMappingURL=index.js.map